import * as React from 'react';
import { useEffect, useState } from 'react';

/** Material UI Packages. */
import { Badge, Box, IconButton, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/** Redux Packages. */
import { useDispatch } from 'react-redux';

/** Redux Reducers. */
import { addStatusMessage, updateStatusMessage } from 'shared/store/slices/statusMessageSlice';
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Internal Components. */
import { DialogButton } from 'shared/ui/Button';
import { createSocketData, updateSocketData } from 'shared/lib/fetchSocket';
import useUserRole from 'shared/hooks/useUserRole';
import { isReadOnlyStatusMessage, shipmentTypeCodes } from './statusMessageRules';

/**
 * Base Input Field options. The user can pick none, one, or up to two of these — selection order
 * matters and is encoded directly into the associate key, e.g. Datetime then Location becomes
 * "associate_datetime_location". None of these keys contain underscores, so parsing an existing
 * associate string back into ordered keys is a plain split on "_".
 */
const BASE_INPUT_FIELD_OPTIONS = [
    { key: 'date', label: 'Date' },
    { key: 'datetime', label: 'Datetime' },
    { key: 'textfield', label: 'Text' },
    { key: 'location', label: 'Location' },
];
const MAX_SELECTED_INPUT_FIELDS = 2;

const associateToFieldKeys = (associate) =>
    (associate ?? '')
        .replace(/^associate_/, '')
        .split('_')
        .filter((key) => BASE_INPUT_FIELD_OPTIONS.some((option) => option.key === key));

const fieldKeysToAssociate = (fieldKeys) => (fieldKeys.length === 0 ? '' : `associate_${fieldKeys.join('_')}`);

/**
 * The user types literal "{}" placeholders directly into the message text — each one marks where an
 * input field's value will be substituted. `fieldKeys` gives the selected fields in click order, which
 * is matched left to right against the "{}" occurrences in `text` (1st "{}" -> fieldKeys[0], etc).
 * `tokenFor` decides what replaces a matched "{}" — the bracketed label for the on-screen preview.
 * Unmatched "{}" (more braces than selected fields) are left as-is.
 */
const applyFieldsToBraces = (text, fieldKeys, tokenFor) => {
    let index = 0;
    return text.replace(/\{\}/g, () => {
        const key = fieldKeys[index];
        index += 1;
        if (!key) return '{}';
        const option = BASE_INPUT_FIELD_OPTIONS.find((candidate) => candidate.key === key);
        return tokenFor(option?.label ?? key);
    });
};

/**
 * The user multi-selects among these 5 checkboxes. Ocean/Air each split into Import/Export; Trucking
 * has no direction. IL_Status_Message_Type stores one (shipment_type, transport_type) row per transport
 * type, so at most 3 rows come out of this (Ocean, Air, Trucking) — checking both Import and Export for
 * the same transport type collapses into a single row with shipment_type: null ("All") rather than two
 * separate rows, e.g. message id 214 → [{null,'Trucking'}, {true,'Air'}, {true,'Ocean'}].
 */
const TYPE_COMBO_OPTIONS = [
    { key: 'ocean_import', label: 'Ocean Import', transport: 'Ocean', direction: true, code: 'OIM' },
    { key: 'ocean_export', label: 'Ocean Export', transport: 'Ocean', direction: false, code: 'OEX' },
    { key: 'air_import', label: 'Air Import', transport: 'Air', direction: true, code: 'AIM' },
    { key: 'air_export', label: 'Air Export', transport: 'Air', direction: false, code: 'AEX' },
    { key: 'trucking', label: 'Trucking', transport: 'Trucking', direction: null, code: 'OTH' },
];

/** selectedCombos (checkbox keys) → the IL_Status_Message_Type row array actually sent to the server. */
const combosToTypes = (selectedCombos) => {
    if (TYPE_COMBO_OPTIONS.every((option) => selectedCombos.includes(option.key))) {
        return [{ shipment_type: null, transport_type: 'All' }];
    }

    const types = [];

    ['Ocean', 'Air'].forEach((transport) => {
        const importSelected = selectedCombos.includes(`${transport.toLowerCase()}_import`);
        const exportSelected = selectedCombos.includes(`${transport.toLowerCase()}_export`);
        if (importSelected && exportSelected) {
            types.push({ shipment_type: null, transport_type: transport });
        } else if (importSelected) {
            types.push({ shipment_type: true, transport_type: transport });
        } else if (exportSelected) {
            types.push({ shipment_type: false, transport_type: transport });
        }
    });

    if (selectedCombos.includes('trucking')) {
        types.push({ shipment_type: null, transport_type: 'Trucking' });
    }

    return types;
};

/** The reverse of combosToTypes, used to restore checkbox state from an existing row's types array. */
const typesToCombos = (types) => {
    const codes = shipmentTypeCodes(types);
    return TYPE_COMBO_OPTIONS.filter((option) => codes.includes(option.code)).map(
        (option) => option.key,
    );
};

/**
 * ToggleButtonGroup collapses shared borders between adjacent buttons (negative margin, no left border on
 * non-first children), which clips the left edge once buttons wrap into pill-shaped chips with a gap.
 * Restore each button's own full border so it doesn't get cut off.
 */
const PRESET_TOGGLE_GROUP_SX = {
    flexWrap: 'wrap',
    gap: '8px',
    '& .MuiToggleButtonGroup-grouped': {
        margin: 0,
        borderLeft: '1px solid rgba(0, 0, 0, 0.23) !important',
    },
};

/** Matches the preset chip style used in Data Entry > Trade Partner's side panel (ApplySetting.js SettingChip). */
const PRESET_TOGGLE_BUTTON_SX = {
    borderRadius: '18px !important',
    textTransform: 'none',
    fontSize: 13,
    minWidth: '50px',
    color: '#c1c1c1',
    backgroundColor: '#ffffff',
    border: '1px solid rgba(0, 0, 0, 0.23)',
    '&:hover': { backgroundColor: '#ffffff', color: '#000000' },
    '&.Mui-selected': { color: '#ffffff', bgcolor: '#00647e' },
    '&.Mui-selected:hover': { color: '#ffffff', bgcolor: '#2488a2' },
};

export default function EditStatusMessagePanel({ open, onClose, row }) {
    const dispatch = useDispatch();
    const userRole = useUserRole();

    /** A row without an id hasn't been created on the server yet. */
    const isNew = !row?.id;
    /**
     * System-provided and code-triggering messages open for reading only. The operator still needs
     * to see which shipment types a message applies to and what it asks for, which lives nowhere
     * else; what they must not do is save a change the server would reject. A brand-new row is
     * never read-only — `row` here is the blank template, which carries no flags.
     */
    const readOnly = !isNew && isReadOnlyStatusMessage(row, userRole);

    const [contentDisplay, setContentDisplay] = useState('');
    /** Selected Input Field keys in click order — the Nth key maps to the Nth "{}" placeholder typed
     *  into contentDisplay. */
    const [fieldKeys, setFieldKeys] = useState([]);
    const [selectedCombos, setSelectedCombos] = useState([]);

    useEffect(() => {
        if (row) {
            setContentDisplay(row.content ?? '');
            setFieldKeys(associateToFieldKeys(row.associate));
            setSelectedCombos(typesToCombos(row.types));
        }
    }, [row]);

    const handleContentChange = (event) => {
        setContentDisplay(event.target.value);
    };

    const handleFieldClick = (key) => {
        setFieldKeys((prev) => {
            if (prev.includes(key)) {
                return prev.filter((existingKey) => existingKey !== key);
            }
            if (prev.length >= MAX_SELECTED_INPUT_FIELDS) return prev;
            return [...prev, key];
        });
    };

    const handleComboToggle = (key) => {
        setSelectedCombos((prev) =>
            prev.includes(key) ? prev.filter((comboKey) => comboKey !== key) : [...prev, key],
        );
    };

    const allCombosSelected = TYPE_COMBO_OPTIONS.every((option) => selectedCombos.includes(option.key));

    const handleSelectAllCombosToggle = () => {
        setSelectedCombos(allCombosSelected ? [] : TYPE_COMBO_OPTIONS.map((option) => option.key));
    };

    const previewText = applyFieldsToBraces(contentDisplay, fieldKeys, (label) => `[${label}]`);

    const handleSave = () => {
        if (readOnly) return;
        const types = combosToTypes(selectedCombos);
        const associate = fieldKeysToAssociate(fieldKeys);
        const content = contentDisplay;

        if (types.length === 0) {
            dispatch(setSnackbar({ open: true, message: 'Select at least one Shipment Type.', severity: 'warning' }));
            return;
        }

        // Mirrors _validate_content's placeholder-count check (status_flow/service.py) so the
        // mismatch is caught before the round trip.
        const placeholderCount = (content.match(/\{\}/g) ?? []).length;
        if (placeholderCount !== fieldKeys.length) {
            dispatch(
                setSnackbar({
                    open: true,
                    message: `Please make sure the number of {} matches the number of input fields.`,
                    severity: 'warning',
                }),
            );
            return;
        }

        if (isNew) {
            // Matches StatusMessageRouter._StatusMessageCreateInput exactly (router.py) — content_display
            // is derived server-side from content, and il_status_message_type rows are created server-side
            // from `types`; neither is a separate socket entity.
            const createPayload = {
                content,
                associate,
                warning: row?.warning ?? false,
                active: row?.active ?? true,
                types,
            };

            createSocketData(dispatch, '', 'row', 'il_status_message', '', createPayload, (receivedData) => {
                dispatch(addStatusMessage(receivedData.data));
                dispatch(
                    setSnackbar({ open: true, message: 'Successfully added the status message.', severity: 'success' }),
                );
                onClose();
            });
            return;
        }

        // Matches StatusMessageRouter._StatusMessageUpdateInput (router.py) — id plus whatever changed;
        // content_display is derived server-side from content.
        const updatePayload = {
            id: row.id,
            content,
            associate,
            warning: row?.warning ?? false,
            active: row?.active ?? true,
            types,
        };

        updateSocketData(dispatch, '', 'row', 'il_status_message', '', updatePayload, (receivedData) => {
            dispatch(updateStatusMessage(receivedData.data));
            dispatch(
                setSnackbar({ open: true, message: 'Successfully updated the status message.', severity: 'success' }),
            );
            onClose();
        });
    };

    return (
        <Box
            sx={{
                width: open ? 360 : 0,
                height: '62vh',
                mt: '80px',
                ml: open ? '15px' : 0,
                overflow: 'hidden',
                transition: 'width 0.3s ease, margin-left 0.3s ease',
                border: open ? 'solid 1px #e2e2e2' : 'none',
                borderRadius: '10px',
                boxShadow: open ? '0 0 6px 0 rgba(0, 0, 0, 0.16)' : 'none',
                flexShrink: 0,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: '10px',
                    py: '8px',
                    bgcolor: '#003a52',
                    borderRadius: '10px 10px 0 0',
                }}
            >
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'white', ml: '10px' }}>
                    {isNew ? 'ADD STATUS MESSAGE' : readOnly ? 'STATUS MESSAGE DETAILS' : 'EDIT STATUS MESSAGE'}
                </Typography>
                <IconButton size="small" onClick={onClose}>
                    <CloseIcon fontSize="small" sx={{ color: 'white' }} />
                </IconButton>
            </Box>

            <Box
                sx={{
                    width: 360,
                    height: 'calc(100% - 56px)',
                    overflowY: 'auto',
                    px: '25px',
                    py: '20px',
                    boxSizing: 'border-box',
                }}
            >
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#003a52', mb: 1 }}>Status Message</Typography>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Use {} to mark where an input field's value goes"
                    value={contentDisplay}
                    onChange={handleContentChange}
                    disabled={readOnly}
                    sx={{
                        mb: 3,
                        '& .MuiOutlinedInput-input': { padding: '8px' },
                        '& .MuiInputBase-input::placeholder': { fontSize: 12 },
                    }}
                />

                <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#003a52', mb: 1 }}>Input Field</Typography>
                <ToggleButtonGroup sx={PRESET_TOGGLE_GROUP_SX}>
                    {BASE_INPUT_FIELD_OPTIONS.map((option) => {
                        const order = fieldKeys.indexOf(option.key) + 1;
                        return (
                            <Badge
                                key={option.key}
                                badgeContent={order || null}
                                color="primary"
                                sx={{ '& .MuiBadge-badge': { right: 6, top: 2 } }}
                            >
                                <ToggleButton
                                    value={option.key}
                                    selected={order > 0}
                                    onClick={() => handleFieldClick(option.key)}
                                    size="small"
                                    disabled={
                                        readOnly ||
                                        (order === 0 &&
                                        (fieldKeys.length >= MAX_SELECTED_INPUT_FIELDS ||
                                            // date and datetime share the same record-table date cell — the server rejects using both together.
                                            (option.key === 'date' && fieldKeys.includes('datetime')) ||
                                            (option.key === 'datetime' && fieldKeys.includes('date'))))
                                    }
                                    sx={PRESET_TOGGLE_BUTTON_SX}
                                >
                                    {option.label}
                                </ToggleButton>
                            </Badge>
                        );
                    })}
                </ToggleButtonGroup>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, mt: 3 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#003a52' }}>Shipment Type</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Typography sx={{ fontSize: 12, color: '#9e9e9e' }}>Select All</Typography>
                        <Switch
                            checked={allCombosSelected}
                            onChange={handleSelectAllCombosToggle}
                            disabled={readOnly}
                            size="small"
                            sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': { color: '#00647e' },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: '#00647e',
                                },
                            }}
                        />
                    </Box>
                </Box>
                <ToggleButtonGroup sx={PRESET_TOGGLE_GROUP_SX}>
                    {TYPE_COMBO_OPTIONS.map((option) => (
                        <ToggleButton
                            key={option.key}
                            value={option.key}
                            selected={selectedCombos.includes(option.key)}
                            onClick={() => handleComboToggle(option.key)}
                            disabled={readOnly}
                            size="small"
                            sx={PRESET_TOGGLE_BUTTON_SX}
                        >
                            {option.label}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>

                <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#003a52', mb: 1, mt: 3 }}>Preview</Typography>
                <Box
                    sx={{
                        p: '8px',
                        borderRadius: '8px',
                        bgcolor: '#f5f5f5',
                        fontSize: 13,
                        color: contentDisplay ? '#003a52' : '#9e9e9e',
                        minHeight: '20px',
                        wordBreak: 'break-word',
                    }}
                >
                    {previewText || 'Type a status message above'}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', mt: 2 }}>
                    <DialogButton text={readOnly ? 'CLOSE' : 'CANCEL'} onClick={onClose} />
                    <DialogButton
                        text="SAVE"
                        onClick={handleSave}
                        additional={{ disabled: readOnly }}
                        sx={{
                            bgcolor: '#00647e',
                            color: 'white',
                            ':hover': { bgcolor: '#003a52' },
                            '&.Mui-disabled': { bgcolor: '#e0e0e0', color: '#9e9e9e' },
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );
}
